<template>
  <div class="basic-date-input-container">
    <!-- Date Picker -->
    <Field
      v-model="formattedDate"
      validateOnInput
      class="basic-date-input-contain"
      :label="label"
      :name="name"
      :rules="validation"
    >
      <div class="form-element-container">
        <div class="bordered-bottom date-input">
          <span>{{ label }} {{ validation.split('|').includes('required') ? '*' : '' }}</span>
          <div class="right-content">
            <DatePicker
              :min-date="min ? new Date(min) : null"
              :max-date="max ? new Date(max) : null"
              is-required
              @dayclick="
                (_, event) => {
                  event.target.blur();
                }
              "
              @update:model-value="onCalendarInput($event)"
            >
              <template #default="{ togglePopover }">
                <input
                  maxlength="10"
                  :value="mutatedValue"
                  :placeholder="datePlaceholder"
                  @input="onInput($event)"
                  @click="togglePopover"
                >
              </template>
            </DatePicker>
            <img
              v-if="mutatedValue"
              class="clear-button"
              src="@/assets/icons/cancel.svg"
              alt="clear input"
              @click="mutatedValue = null;$emit('addData', { value: '', name })"
            >
          </div>
        </div>
      </div>
      <ErrorMessage
        class="error-msg"
        :name="name"
      />
    </Field>
  </div>
</template>

<script>
  /**
   * Props:
   * name: input name (string)
   * label: Title displayed: e.g.:'Selectionner une option: ' (string)
   * valueDate: the date that'll need to be displayed on loading e.g.: 2018-07-15 (string)
   */
import moment from 'moment'
import { Field, ErrorMessage } from 'vee-validate'
import { DatePicker } from 'v-calendar'
import 'v-calendar/style.css'

export default {
  name: 'BasicDateInput',
  components: { Field, ErrorMessage, DatePicker },
  props: {
    label: {
      type: String,
      default: '',
      required: true
    },
    placeholder: {
      type: String,
      default: '',
      required: true
    },
    validation: {
      type: [String, Boolean],
      default: null
    },
    name: {
      type: String,
      default: '',
      required: true
    },
    min: {
      type: String,
      default: null
    },
    max: {
      type: String,
      default: null
    },
    defaultValue: {
      type: [Date, String],
      default: null
    }
  },
  emits: ['addData'],
  data() {
    return {
      mutatedValue: this.defaultValue
        ? moment(this.defaultValue, ['DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD']).format('YYYY-MM-DD')
        : null,
      moment
    }
  },
  computed: {
    datePlaceholder() {
      return moment(new Date()).localeData().longDateFormat('L').toLowerCase()
    },
    formattedDate() {
      return this.mutatedValue ? moment(this.mutatedValue, ['L']).format('YYYY-MM-DD') : null
    }
  },
  mounted() {
    this.validationAddData()
    if (this.defaultValue) {
      if (this.defaultValue === 'now') {
        this.mutatedValue = moment()
      } else if (this.defaultValue.includes('now-')) {
        // Format : 'now-1' : now minus 1 day
        // 'now-2' : now minus 2 days
        const nbDays = this.defaultValue.split('-')[1]
        this.mutatedValue = moment().subtract(nbDays, 'days')
      } else if (this.defaultValue.includes('now+')) {
        // Format : 'now+1' : now plus 1 day
        // 'now+2' : now plus 2 days
        const nbDays = this.defaultValue.split('+')[1]
        this.mutatedValue = moment().add(nbDays, 'days')
      } else {
        this.mutatedValue = this.defaultValue
      }
    }
    if (this.mutatedValue) {
      this.mutatedValue = moment(this.mutatedValue, ['DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD']).format(
        'L'
      )
      this.validationAddData()
    }
  },
  methods: {
    onCalendarInput(event) {
      this.mutatedValue = moment(event).format('L')

      this.validationAddData()
    },
    onInput(event) {
      let date = event?.target?.value
      if (moment(date, 'L')?.creationData()?.locale?._longDateFormat?.L.startsWith('YYYY')) { // case where date format start with year
        if (date.length >= 4) {
          this.mutatedValue = date
            .replace(/^(\d\d\d\d)(\d)$/g, '$1/$2')
            .replace(/^(\d\d\d\d\/\d\d)(\d+)$/g, '$1/$2')
            .replace(/[^\d\/]/g, '')
        }
      } else { // other cases
        this.mutatedValue = date
          .replace(/^(\d\d)(\d)$/g, '$1/$2')
          .replace(/^(\d\d\/\d\d)(\d+)$/g, '$1/$2')
          .replace(/[^\d\/]/g, '')
      }

      this.validationAddData()
    },
    validationAddData() {
      if (this.mutatedValue) {
        this.$emit('addData', {
          value: moment(this.mutatedValue, ['L']).format('YYYY-MM-DD'),
          name: this.name
        })
      } else {
        this.$emit('addData', { value: '', name: this.name })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .basic-date-input-container {
    display: flex;
    justify-content: flex-start;
    flex-direction: column;
    margin: 10px 0;

    .basic-date-input-contain {
      width: 100%;
    }

    /* Tablet (portrait) */
    @media only screen and (width >= 768px) {
      margin: 15px 0;
    }

    /* Desktops and laptops */
    @media only screen and (width >= 1024px) {
      margin: 15px 0;
    }

    /* Large screens */
    @media only screen and (width >= 1824px) {
      margin: 15px 0;
    }

    .form-element-container {
      width: 100%;
    }

    label {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      white-space: nowrap;
      align-items: center;
      padding-left: 0.5rem;

      span {
        font-weight: bolder;
        font-size: inherit !important;
      }
    }

    input {
      text-align: right;
      border: none;
    }
  }

  .date-input {
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 0.25rem 0 0.25rem 0.5rem;
    align-items: center;
    text-align: left;
  }

  input::-webkit-calendar-picker-indicator {
    appearance: none;
    height: 1rem;
    width: 1rem;
    background-image: url('@/assets/icons/arrow-select.png');
    background-color: transparent;
    background-position: center right;
    background-origin: content-box;
    background-repeat: no-repeat;
    background-size: contain;
    color: transparent;
  }

  input::-webkit-clear-button {
    appearance: none;
    height: 1rem;
    width: 1rem;
    background-image: url('@/assets/icons/close.png');
    background-position: center right;
    background-origin: content-box;
    background-repeat: no-repeat;
    background-size: contain;
  }

  .right-content {
    display: flex;
    align-items: center;

    .clear-button {
      margin-left: 0.3rem;
      width: 0.8rem;
      height: 0.8rem;
    }
  }
</style>
